/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import { action, computed } from '@ember/object';
import { and } from '@ember/object/computed';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import isDestroyed from 'cio/utils/is-destroyed';
import { Events, FlyDragEvent } from 'fly-drag-drop/utils/events';
import { inject as service } from '@ember/service';
import FlyDragDropManagerService from 'fly-drag-drop/services/fly-drag-drop-manager';
import GroupCondition from 'cio/app-models/conditions/group';
import { MainConditionInterface } from 'cio/app-models/conditions/abstract';

const PLACEHOLDER_OFFSETS = {
  width: 0,
  height: 0,
  top: 4,
  left: -10,
};

// Check whether a given group has other groups amongst its children.
let hasGroupsInChildren = (group: GroupCondition, distanceToMaxDepth: number) => {
  let childGroups = group.children.filter((child) => child?.type === 'group') as GroupCondition[];

  if (childGroups.length === 0) {
    return false;
  }

  let nextDepth = distanceToMaxDepth - 1;

  // If we're one level above the maximum depth, no need to check deeper,
  // because if this group has other groups within it, then we'll be exceeding
  // the maximum depth already.
  if (nextDepth === 0) {
    return true;
  }

  // If we're further away from the max depth, check this group's child groups
  // for even deeper groups.
  let childGroupsHaveGroups = childGroups.some((childGroup) => hasGroupsInChildren(childGroup, nextDepth));
  return childGroupsHaveGroups;
};

interface ConditionEditorGroupSortableListArgs {
  condition: GroupCondition;
  depth?: number;
  maxDepth?: number;
  groupName: string;
  items: MainConditionInterface[];
  onDropAction: () => unknown;
}

export default class ConditionEditorGroupSortableList extends Component<ConditionEditorGroupSortableListArgs> {
  @service flyDragDropManager!: FlyDragDropManagerService;

  get depth() {
    return this.args.depth ?? 0;
  }

  get maxDepth() {
    return this.args.maxDepth ?? 2;
  }
  @and('flyDragDropManager.isDragging', 'canDrop', 'isItemsEmpty') isDraggingAndEmpty!: boolean;

  @computed('args.items.[]')
  get isItemsEmpty() {
    return (this.args.items ?? []).length === 0;
  }

  @tracked canDrop = true;

  @computed('isItemsEmpty')
  get pluginOptions() {
    let classes = [
      'cio-condition-editor-group-sortable-placeholder',
      this.isItemsEmpty ? 'is-empty-condition' : null,
    ].compact();

    return {
      sortableListPlaceholder: {
        classes,
        offsets: PLACEHOLDER_OFFSETS,
        changeOffsetSignDependingOnPosition: true,
      },
    };
  }

  constructor(owner: unknown, args: ConditionEditorGroupSortableListArgs) {
    super(owner, args);

    this.flyDragDropManager.on(Events.DragStart, this.onDragStart);
    this.flyDragDropManager.on(Events.DragEnd, this.onDragEnd);
  }

  willDestroy() {
    super.willDestroy();

    this.flyDragDropManager.off(Events.DragStart, this.onDragStart);
    this.flyDragDropManager.off(Events.DragEnd, this.onDragEnd);
  }

  @action
  onDragStart(event: FlyDragEvent) {
    if (!isDestroyed(this)) {
      let draggedCondition = event.detail.context.item;
      let draggingType = draggedCondition.type;

      // Dropping non-groups is fine at any level.
      if (draggingType !== 'group') {
        this.canDrop = true;
        return;
      }
      // Don't allow dropping a group into itself
      if (draggedCondition === this.args.condition) {
        this.canDrop = false;
        return;
      }

      let distanceToMaxDepth = this.maxDepth - this.depth;

      // If we're already at maximum depth, don't allow dropping further groups.
      if (distanceToMaxDepth === 0) {
        this.canDrop = false;
        return;
      }

      // If we're above maximum depth, check whether this group's children, and
      // its children's children, contain groups that are deep enough to violate
      // the maxDepth limit.
      let draggedItemHasGroups = hasGroupsInChildren(draggedCondition, distanceToMaxDepth);
      this.canDrop = !draggedItemHasGroups;
    }
  }

  @action
  onDragEnd() {
    if (!isDestroyed(this)) {
      this.canDrop = true;
    }
  }
}
